<template>
    <SectionsList
        v-if="!showSectionDetails && !showQuestionDetails"
        @editSection="editSection($event)"
        @addSection="createSection"
        @editQuestion="editQuestion($event)"
        @addQuestion="createQuestion($event)"
    />
    <SectionDetails
        v-if="showSectionDetails"
        :section="sectionToEdit"
        :backupData="backupData"
        :mode="mode"
        @cancel="cancelSection"
        @save="saveSection"
    />
    <QuestionDetails
        v-if="showQuestionDetails"
        :question="questionToEdit"
        :backupData="backupData"
        :mode="mode"
        @cancel="cancelQuestion"
        @save="saveQuestion"
    />
</template>

<script>
import { ref } from "vue";
import { mapState, mapMutations, mapActions } from "vuex";
import SectionsList from "./questions/SectionsList.vue";
import SectionDetails from "./questions/SectionDetails.vue";
import QuestionDetails from "./questions/QuestionDetails.vue";
import { useBackupData } from "@/shared/utils/forms/useBackupData";
import { useCleanQueryParams } from "@/shared/utils/router/useCleanQueryParams";
import { useVuexSideEffect } from "@/store/useVuexSideEffect";

const EMPTY_QUESTION = (section) => ({
    answer: "",
    question: "",
    sectionId: section.id,
});

const MODE = {
    create: "create",
    edit: "edit",
};

export default {
    components: {
        SectionsList,
        SectionDetails,
        QuestionDetails,
    },
    setup() {
        const mode = ref(MODE.create);
        const showSectionDetails = ref(false);
        const showQuestionDetails = ref(false);
        const sectionToEdit = ref({});
        const questionToEdit = ref({});
        const { cleanQueryParams } = useCleanQueryParams();
        const { backupData, setBackupValue } = useBackupData({});
        useVuexSideEffect({
            // bring back view if publish in meantime... (avoid "save changes" button being inconsistent)
            setPublished: () => {
                showSectionDetails.value = false;
                showQuestionDetails.value = false;
            },
        });
        return {
            backupData,
            cleanQueryParams,
            mode,
            questionToEdit,
            sectionToEdit,
            setBackupValue,
            showQuestionDetails,
            showSectionDetails,
        };
    },
    mounted() {
        this.$watch(() => this.$route.query.open, this.handleQueryChange, {
            immediate: true,
        });
    },
    computed: {
        ...mapState("faq", ["faq", "sections"]),
    },
    methods: {
        ...mapMutations("faq", [
            "addSection",
            "addQuestion",
            "changeQuestion",
            "removeSection",
            "changeSection",
        ]),
        ...mapActions("faq", ["removeQuestion"]),
        ...mapActions("faq/projects", ["saveSnapshot"]),
        ...mapActions("alert", ["showWarnMessage"]),
        editSection(section) {
            this.mode = MODE.edit;
            this.sectionToEdit = section;
            this.setBackupValue(this.sectionToEdit);
            this.showSectionDetails = true;
        },
        createSection() {
            this.mode = MODE.create;
            this.addSection("");
            this.sectionToEdit = this.sections[this.sections.length - 1];
            this.setBackupValue(this.sectionToEdit);
            this.showSectionDetails = true;
        },
        saveSection() {
            if (!this.sectionToEdit._created && !this.sectionToEdit._deleted) {
                this.sectionToEdit._changed = true;
            }
            this.saveSnapshot();
            this.showSectionDetails = false;
        },
        async cancelSection() {
            if (this.mode === MODE.edit) {
                this.changeSection(this.backupData);
            } else {
                this.removeSection(this.sectionToEdit);
            }
            await this.saveSnapshot();
            this.showSectionDetails = false;
        },
        editQuestion(question) {
            this.mode = MODE.edit;
            this.questionToEdit = question;
            this.setBackupValue(this.questionToEdit);
            this.showQuestionDetails = true;
        },
        createQuestion(section) {
            this.mode = MODE.create;
            const currentSection = section ? section : this.sections[0];
            const emptyQuestion = EMPTY_QUESTION(currentSection);
            this.addQuestion(emptyQuestion);
            this.questionToEdit = currentSection.questions[currentSection.questions.length - 1];
            this.setBackupValue(this.questionToEdit);

            this.showQuestionDetails = true;
        },
        saveQuestion() {
            if (!this.questionToEdit._created && !this.questionToEdit._deleted) {
                this.questionToEdit._changed = true;
            }
            this.saveSnapshot();
            this.showQuestionDetails = false;
            this.cleanQueryParams(["open"]);
        },
        async cancelQuestion() {
            if (this.mode === MODE.edit) {
                this.changeQuestion(this.backupData);
            } else {
                this.removeQuestion(this.questionToEdit);
            }
            await this.saveSnapshot();
            this.showQuestionDetails = false;
            this.cleanQueryParams(["open"]);
        },
        handleQueryChange() {
            if (this.$route.query.open === MODE.create) {
                this.createQuestion();
                setTimeout(() => {
                    this.cleanQueryParams(["open"]);
                }, 100);
            }
            if (this.$route.query.open === "edit-section") {
                const sectionId = +this.$route.query.id;
                setTimeout(() => {
                    this.cleanQueryParams(["open", "id"]);
                }, 100);
                const section = this.sections.find((s) => s.id === sectionId);
                if (!section) {
                    return;
                }
                if (this.showSectionDetails && this.sectionToEdit.id !== sectionId) {
                    this.showWarnMessage({
                        message: this.$t("faq.questions.unsaved_changes_section"),
                    });

                    return;
                }
                if (this.showQuestionDetails) {
                    this.showWarnMessage({
                        message: this.$t("faq.questions.unsaved_changes_question"),
                    });

                    return;
                }
                this.editSection(section);
            }
            if (this.$route.query.open === "edit-question") {
                const questionId = +this.$route.query.id;
                setTimeout(() => {
                    this.cleanQueryParams(["open", "id"]);
                }, 100);
                const question = this.sections
                    .flatMap((s) => s.questions)
                    .find((q) => q.id === questionId);

                if (!question) {
                    return;
                }
                if (this.showQuestionDetails && this.questionToEdit.id !== questionId) {
                    this.showWarnMessage({
                        message: this.$t("faq.questions.unsaved_changes_question"),
                    });

                    return;
                }
                if (this.showSectionDetails) {
                    this.showWarnMessage({
                        message: this.$t("faq.questions.unsaved_changes_section"),
                    });

                    return;
                }

                this.editQuestion(question);
            }
        },
    },
};
</script>
